import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, MoreVertical, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OrderOverview } from "@/hooks/useOrderOverview";
import { ProductsCell } from "./ProductsCell";
import { SalespersonsCell } from "./SalespersonsCell";
import { AddHoursModal } from "./AddHoursModal";

interface OrderOverviewTableProps {
  data: OrderOverview[];
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (column: string) => void;
  onRefresh?: () => void;
}

export const OrderOverviewTable = ({
  data,
  sortBy,
  sortOrder,
  onSort,
  onRefresh,
}: OrderOverviewTableProps) => {
  const [selectedOrder, setSelectedOrder] = useState<{
    code: string;
    name: string;
    orderId: string;
    manualAddHoursAt: string | null;
    manualAddHourBy: string | null;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [manualInfo, setManualInfo] = useState<{
    value: number;
    by: string | null;
    at: string | null;
  } | null>(null);
  const [isManualInfoOpen, setIsManualInfoOpen] = useState(false);

  const handleAddHours = (order: OrderOverview) => {
    if (!order.fishsix_student_code) return;

    setSelectedOrder({
      code: order.fishsix_student_code,
      name: order.customer_name || "-",
      orderId: order.id,
      manualAddHoursAt: order.manual_add_hours_at,
      manualAddHourBy: order.manual_add_hour_by,
    });
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setSelectedOrder(null);
    }
  };

  const handleModalSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };
  const getStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="secondary">-</Badge>;

    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      Confirmed: "default",
      Pending: "secondary",
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {status}
      </Badge>
    );
  };

  const getOrderTypeBadge = (type: string | null) => {
    if (!type) return <span className="text-muted-foreground">-</span>;

    return (
      <Badge variant={type === "NEW" ? "default" : "outline"}>
        {type}
      </Badge>
    );
  };

  const handleManualInfoOpen = (order: OrderOverview) => {
    if (!order.manual_add_hours_at || order.manual_add_hours_value === null) {
      return;
    }

    setManualInfo({
      value: order.manual_add_hours_value,
      by: order.manual_add_hour_by,
      at: order.manual_add_hours_at,
    });
    setIsManualInfoOpen(true);
  };

  const SortButton = ({ column, children }: { column: string; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      onClick={() => onSort(column)}
      className="h-8 px-2 hover:bg-transparent"
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortButton column="order_code">รหัสคำสั่งซื้อ</SortButton>
            </TableHead>
            <TableHead>
              <SortButton column="customer_name">ชื่อลูกค้า</SortButton>
            </TableHead>
            <TableHead>ประเภท</TableHead>
            <TableHead>สถานะ</TableHead>
            <TableHead>รหัสนักเรียน</TableHead>
            <TableHead>สินค้า</TableHead>
            <TableHead>พนักงานขาย</TableHead>
            <TableHead className="text-center">เพิ่มชั่วโมงเอง</TableHead>
            <TableHead className="text-center">การจัดการ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="h-24 text-center">
                ไม่พบข้อมูลคำสั่งซื้อ
              </TableCell>
            </TableRow>
          ) : (
            data.map((order) => (
              <TableRow key={order.id}>
                <TableCell className="font-medium">
                  {order.order_code || "-"}
                </TableCell>
                <TableCell>{order.customer_name || "-"}</TableCell>
                <TableCell>{getOrderTypeBadge(order.order_type)}</TableCell>
                <TableCell>{getStatusBadge(order.status)}</TableCell>
                <TableCell>{order.fishsix_student_code || "-"}</TableCell>
                <TableCell>
                  <ProductsCell products={order.products} />
                </TableCell>
                <TableCell>
                  <SalespersonsCell salespersons={order.salespersons} />
                </TableCell>
                <TableCell className="text-center">
                  {order.manual_add_hours_at && order.manual_add_hours_value !== null ? (
                    <button
                      type="button"
                      onClick={() => handleManualInfoOpen(order)}
                      className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Badge variant="secondary" className="cursor-pointer">
                        {order.manual_add_hours_value > 0 ? "+" : ""}
                        {order.manual_add_hours_value} ชม.
                      </Badge>
                    </button>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleAddHours(order)}
                        disabled={!order.fishsix_student_code}
                      >
                        <Clock className="mr-2 h-4 w-4" />
                        เพิ่ม/ลดชั่วโมง
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {selectedOrder && (
        <AddHoursModal
          open={isModalOpen}
          onOpenChange={handleModalOpenChange}
          studentCode={selectedOrder.code}
          studentName={selectedOrder.name}
          orderId={selectedOrder.orderId}
          manualAddHoursAt={selectedOrder.manualAddHoursAt}
          manualAddHourBy={selectedOrder.manualAddHourBy}
          onSuccess={handleModalSuccess}
        />
      )}

      <Dialog open={isManualInfoOpen} onOpenChange={setIsManualInfoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>รายละเอียดการเพิ่มชั่วโมง</DialogTitle>
            <DialogDescription>
              {manualInfo?.at
                ? new Date(manualInfo.at).toLocaleString("th-TH", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "-"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div>
              จำนวนชั่วโมง:{" "}
              <span className="font-medium">
                {manualInfo
                  ? `${manualInfo.value > 0 ? "+" : ""}${manualInfo.value} ชม.`
                  : "-"}
              </span>
            </div>
            <div>
              ผู้ดำเนินการ:{" "}
              <span className="font-medium">
                {manualInfo?.by || "ไม่ทราบ"}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
